import {
  type CSSProperties,
  type ReactNode,
  useRef,
  useState,
  useCallback,
} from "react";
import { flexRender, type Table, type Header } from "@tanstack/react-table";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  GripVertical,
  Pin,
  ChevronsDown,
  ChevronsRight,
  MoreHorizontal,
} from "lucide-react";

import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  type DragEndEvent,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToHorizontalAxis } from "@dnd-kit/modifiers";
import {
  SortableContext,
  horizontalListSortingStrategy,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import {
  TableHead,
  TableHeader as TableHeaderPrimitive,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { cn } from "@/lib/utils";
import { getPinningStyles } from "../utils";

interface TableHeaderProps<TData> {
  table: Table<TData>;
  enableRowDragAndDrop?: boolean;
  enableColumnDragAndDrop?: boolean;
  enablePinning?: boolean;
  enableResizing?: boolean;
  enableSorting?: boolean;
  enableSelection?: boolean;
  enableExpanding?: boolean;
  subRowKey?: keyof TData;
  headerActions?: (columnId: string) => ReactNode;
  onColumnOrderChange?: (newOrder: string[]) => void;
}

interface SelectionHeaderCellProps<TData> {
  table: Table<TData>;
  enableRowDragAndDrop?: boolean;
  enableExpanding?: boolean;
}

function SelectionHeaderCell<TData>({
  table,
  enableRowDragAndDrop,
  enableExpanding,
}: SelectionHeaderCellProps<TData>) {
  const isAllSelected = table.getIsAllPageRowsSelected();
  const isSomeSelected = table.getIsSomePageRowsSelected();

  return (
    <div className="aero:flex aero:items-center aero:gap-2">
      {enableRowDragAndDrop && (
        <div className="aero:cursor-not-allowed aero:text-neutral-400">
          <GripVertical className="aero:h-4 aero:w-4" />
        </div>
      )}
      <Checkbox
        checked={
          isAllSelected ? true : isSomeSelected ? "indeterminate" : false
        }
        onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
        aria-label="Select all"
        className="aero:cursor-pointer"
      />
      {enableExpanding && (
        <Button
          design="text"
          size="iconSm"
          onClick={table.getToggleAllRowsExpandedHandler()}
          className="aero:h-6 aero:w-6 aero:p-0"
        >
          {table.getIsAllRowsExpanded() ? (
            <ChevronsDown className="aero:h-4 aero:w-4" />
          ) : (
            <ChevronsRight className="aero:h-4 aero:w-4" />
          )}
        </Button>
      )}
    </div>
  );
}

interface SortableHeaderCellContentProps<TData> {
  header: Header<TData, unknown>;
  enableSorting?: boolean;
  enablePinning?: boolean;
  enableColumnDragAndDrop?: boolean;
  dragAttributes?: any;
  dragListeners?: any;
  headerActions?: ReactNode;
}

function SortableHeaderCellContent<TData>({
  header,
  enableSorting,
  enablePinning,
  enableColumnDragAndDrop,
  dragAttributes,
  dragListeners,
  headerActions,
}: SortableHeaderCellContentProps<TData>) {
  const column = header.column;
  const canSort = enableSorting && column.getCanSort();
  const isPinned = column.getIsPinned();
  const sortDirection = column.getIsSorted();

  // Special handling for number order column
  const isNumOrderColumn = column.id === "no";
  const showPinMenu = enablePinning && !isNumOrderColumn;

  const SortIcon = () => {
    if (!sortDirection)
      return <ArrowUpDown className="aero:h-4 aero:w-4 aero:opacity-50" />;
    return sortDirection === "asc" ? (
      <ArrowUp className="aero:h-4 aero:w-4" />
    ) : (
      <ArrowDown className="aero:h-4 aero:w-4" />
    );
  };

  const handleSort = () => {
    if (!canSort) return;
    if (!sortDirection) {
      column.toggleSorting(false);
    } else if (sortDirection === "asc") {
      column.toggleSorting(true);
    } else {
      column.clearSorting();
    }
  };

  return (
    <div
      className={cn(
        isNumOrderColumn
          ? "aero:justify-center aero:mx-auto"
          : "aero:flex aero:items-center aero:gap-1 aero:group aero:pr-6",
      )}
    >
      {enableColumnDragAndDrop && !isNumOrderColumn && (
        <div
          className="aero:cursor-grab aero:text-neutral-400 aero:hover:text-neutral-600 aero:transition-opacity"
          {...dragAttributes}
          {...dragListeners}
        >
          <GripVertical className="aero:h-4 aero:w-4" />
        </div>
      )}

      <Button
        design="text"
        size="sm"
        onClick={handleSort}
        className={cn(
          "aero:h-8 aero:-ml-2 aero:hover:bg-brand-primary-100",
          !canSort && "aero:cursor-default aero:hover:bg-transparent",
        )}
      >
        <span className="aero:font-bold aero:text-sm aero:text-foreground">
          {flexRender(column.columnDef.header, header.getContext())}
        </span>
        {canSort && <SortIcon />}
        {isPinned && (
          <Pin className="aero:h-3 aero:w-3 aero:text-brand-primary-600 aero:fill-brand-primary-600 aero:ml-1" />
        )}
      </Button>

      {(showPinMenu || headerActions) && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              design="text"
              size="iconSm"
              className="aero:h-5 aero:w-5 aero:p-0 aero:hover:bg-brand-primary-100 aero:relative aero:z-10"
            >
              <MoreHorizontal className="aero:h-4 aero:w-4 aero:text-neutral-500" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="start"
            className="aero:min-w-[140px] aero:z-50"
          >
            {showPinMenu &&
              (isPinned ? (
                <DropdownMenuItem
                  onSelect={(e) => {
                    e.preventDefault();
                    column.pin(false);
                  }}
                  className="aero:cursor-pointer"
                >
                  <Pin className="aero:h-4 aero:w-4 aero:mr-2" />
                  Unpin Column
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem
                  onSelect={(e) => {
                    e.preventDefault();
                    column.pin("left");
                  }}
                  className="aero:cursor-pointer"
                >
                  <Pin className="aero:h-4 aero:w-4 aero:mr-2" />
                  Pin to Left
                </DropdownMenuItem>
              ))}
            {enablePinning && headerActions && <DropdownMenuSeparator />}
            {headerActions}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}

interface ResizeHandleProps<TData> {
  header: Header<TData, unknown>;
  table: Table<TData>;
  tableRef?: React.RefObject<HTMLTableElement>;
}

function ResizeHandle<TData>({
  header,
  table,
  tableRef,
}: ResizeHandleProps<TData>) {
  const [isResizing, setIsResizing] = useState(false);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);
  const nextColumnStartWidthRef = useRef(0);
  const tableWidthRef = useRef(0);

  const columnId = header.column.id;
  const minWidthPercent = 3; // Minimum column width as percentage of table width

  const getNextResizableColumn = useCallback(() => {
    const allColumns = table.getAllLeafColumns();
    const currentIndex = allColumns.findIndex((col) => col.id === columnId);

    for (let i = currentIndex + 1; i < allColumns.length; i++) {
      const col = allColumns[i];

      if (col.id === "select" || col.id === "actions") continue;

      if (!col.getCanResize()) continue;
      return col;
    }
    return null;
  }, [table, columnId]);

  const getTableElement = useCallback((): HTMLTableElement | null => {
    if (tableRef?.current) return tableRef.current;

    const headerElement = document.querySelector(
      `[data-column-id="${columnId}"]`,
    );
    return headerElement?.closest("table") ?? null;
  }, [tableRef, columnId]);

  const pixelToPercent = useCallback(
    (pixels: number, tableWidth: number): number => {
      return (pixels / tableWidth) * 100;
    },
    [],
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      const nextColumn = getNextResizableColumn();
      const tableElement = getTableElement();
      const tableWidth = tableElement?.offsetWidth ?? 1000;

      setIsResizing(true);
      startXRef.current = e.clientX;
      tableWidthRef.current = tableWidth;

      startWidthRef.current = header.getSize();
      nextColumnStartWidthRef.current = nextColumn?.getSize() ?? 0;

      const handleMouseMove = (moveEvent: MouseEvent) => {
        const delta = moveEvent.clientX - startXRef.current;
        const currentTableWidth = tableWidthRef.current;

        const newWidthPx = Math.max(
          (minWidthPercent / 100) * currentTableWidth,
          startWidthRef.current + delta,
        );

        const currentSizing = table.getState().columnSizing;
        const newSizing: Record<string, number> = {
          ...currentSizing,
          [columnId]: newWidthPx,
        };

        table.setColumnSizing(newSizing);
      };

      const handleMouseUp = () => {
        setIsResizing(false);
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
      };

      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    },
    [
      header,
      table,
      columnId,
      minWidthPercent,

      getNextResizableColumn,
      getTableElement,
      pixelToPercent,
    ],
  );

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      e.preventDefault();
      e.stopPropagation();

      const nextColumn = getNextResizableColumn();
      const tableElement = getTableElement();
      const tableWidth = tableElement?.offsetWidth ?? 1000;

      setIsResizing(true);
      const touch = e.touches[0];
      startXRef.current = touch.clientX;
      tableWidthRef.current = tableWidth;
      startWidthRef.current = header.getSize();
      nextColumnStartWidthRef.current = nextColumn?.getSize() ?? 0;

      const handleTouchMove = (moveEvent: TouchEvent) => {
        const touch = moveEvent.touches[0];
        const delta = touch.clientX - startXRef.current;
        const currentTableWidth = tableWidthRef.current;

        const newWidthPx = Math.max(
          (minWidthPercent / 100) * currentTableWidth,
          startWidthRef.current + delta,
        );
        const actualDelta = newWidthPx - startWidthRef.current;

        const currentSizing = table.getState().columnSizing;
        const newSizing: Record<string, number> = {
          ...currentSizing,
          [columnId]: newWidthPx,
        };

        const nextColumn = getNextResizableColumn();
        if (nextColumn && nextColumnStartWidthRef.current > 0) {
          const nextMinWidthPx = (minWidthPercent / 100) * currentTableWidth;
          const nextNewWidthPx = Math.max(
            nextMinWidthPx,
            nextColumnStartWidthRef.current - actualDelta,
          );

          if (nextNewWidthPx >= nextMinWidthPx) {
            newSizing[nextColumn.id] = nextNewWidthPx;
          } else {
            const maxDelta = nextColumnStartWidthRef.current - nextMinWidthPx;
            const clampedWidthPx = startWidthRef.current + maxDelta;
            newSizing[columnId] = clampedWidthPx;
            newSizing[nextColumn.id] = nextMinWidthPx;
          }
        }

        table.setColumnSizing(newSizing);
      };

      const handleTouchEnd = () => {
        setIsResizing(false);
        document.removeEventListener("touchmove", handleTouchMove);
        document.removeEventListener("touchend", handleTouchEnd);
      };

      document.addEventListener("touchmove", handleTouchMove, {
        passive: false,
      });
      document.addEventListener("touchend", handleTouchEnd);
    },
    [
      header,
      table,
      columnId,
      minWidthPercent,
      getNextResizableColumn,
      getTableElement,
      pixelToPercent,
    ],
  );

  const handleDoubleClick = useCallback(() => {
    header.column.resetSize();
  }, [header]);

  return (
    <div
      onDoubleClick={handleDoubleClick}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
      className={cn(
        "aero:absolute aero:right-0 aero:top-0 aero:h-full aero:w-5",
        "aero:cursor-col-resize aero:select-none aero:touch-none",
        "aero:flex aero:items-center aero:justify-center aero:transition-colors",
      )}
    >
      <div
        className={cn(
          "aero:flex aero:items-center aero:justify-center aero:transition-colors",
          isResizing && "aero:bg-brand-primary-300",
          "aero:bg-transparent aero:hover:bg-brand-primary-100 aero:rounded-sm aero:p-0.5",
        )}
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          className="aero:text-muted-foreground aero:hover:text-brand-primary-600"
        >
          {/* Left arrow */}
          <path
            d="M7 12L3 12M3 12L5.5 9.5M3 12L5.5 14.5"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          {/* Right arrow */}
          <path
            d="M17 12L21 12M21 12L18.5 9.5M21 12L18.5 14.5"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          {/* Center dots */}
          <circle cx="10" cy="12" r="1.5" fill="currentColor" />
          <circle cx="14" cy="12" r="1.5" fill="currentColor" />
        </svg>
      </div>
    </div>
  );
}

interface DraggableHeaderCellProps<TData> {
  header: Header<TData, unknown>;
  table: Table<TData>;
  enableSorting?: boolean;
  enablePinning?: boolean;
  enableResizing?: boolean;
  enableSelection?: boolean;
  enableColumnDragAndDrop?: boolean;
  enableRowDragAndDrop?: boolean;
  enableExpanding?: boolean;
  subRowKey?: keyof TData;
  headerActions?: (columnId: string) => ReactNode;
  onColumnResize?: (columnId: string, width: number) => void;
}

function DraggableHeaderCell<TData>({
  header,
  table,
  enableSorting,
  enablePinning,
  enableResizing,
  enableSelection,
  enableColumnDragAndDrop,
  enableRowDragAndDrop,
  enableExpanding,
  subRowKey,
  headerActions,
}: DraggableHeaderCellProps<TData>) {
  const column = header.column;
  const isPinned = column.getIsPinned();
  const isSelectColumn = column.id === "select";
  const isActionsColumn = column.id === "actions";
  const isSystemColumn = isSelectColumn || isActionsColumn;

  const isLastResizableColumn = (() => {
    const allColumns = table.getAllLeafColumns();
    const currentIndex = allColumns.findIndex((col) => col.id === column.id);

    for (let i = currentIndex + 1; i < allColumns.length; i++) {
      const col = allColumns[i];

      if (col.id === "select" || col.id === "actions") continue;

      if (col.getCanResize()) return false;
    }
    return true;
  })();

  const { attributes, isDragging, listeners, setNodeRef, transform } =
    useSortable({
      id: column.id,
      disabled: isSystemColumn,
    });

  const style: CSSProperties = {
    ...getPinningStyles(column),
    opacity: isDragging ? 0.8 : 1,
    position: isPinned ? "sticky" : "relative",
    transform: CSS.Translate.toString(transform),
    transition: "width transform 0.2s ease-in-out",
    whiteSpace: "nowrap",
    width: header.getSize(),
    zIndex: isDragging ? 1000 : isPinned ? 1 : 0,
  };

  return (
    <TableHead
      ref={setNodeRef}
      colSpan={header.colSpan}
      style={style}
      className={cn(
        "aero:relative aero:group aero:whitespace-nowrap aero:h-11 aero:px-2 aero:py-3 aero:border-y aero:!border-neutral-400",
        "aero:text-sm aero:font-bold aero:text-foreground aero:!bg-brand-primary-50",
        isPinned && "aero:bg-brand-primary-50",
        isDragging && "aero:shadow-lg aero:ring-2 aero:ring-brand-primary-300",
      )}
    >
      {header.isPlaceholder ? null : (
        <div className="aero:flex aero:items-center aero:gap-3">
          {isSelectColumn && enableSelection ? (
            <SelectionHeaderCell
              table={table}
              enableRowDragAndDrop={enableRowDragAndDrop}
              enableExpanding={enableExpanding && !!subRowKey}
            />
          ) : isActionsColumn ? (
            <div className="aero:flex aero:items-center aero:justify-center aero:w-full">
              <span className="aero:font-bold aero:text-sm aero:text-foreground">
                {flexRender(column.columnDef.header, header.getContext())}
              </span>
            </div>
          ) : (
            <SortableHeaderCellContent
              header={header}
              enableSorting={enableSorting && column.getCanSort()}
              enablePinning={enablePinning}
              enableColumnDragAndDrop={enableColumnDragAndDrop}
              dragAttributes={attributes}
              dragListeners={listeners}
              headerActions={headerActions && headerActions(column.id)}
            />
          )}

          {enableResizing &&
            !isSystemColumn &&
            !isLastResizableColumn &&
            column.getCanResize() && (
              <ResizeHandle header={header} table={table} />
            )}
        </div>
      )}
    </TableHead>
  );
}

export function DataTableHeader<TData>({
  table,
  enableRowDragAndDrop = false,
  enableColumnDragAndDrop = false,
  enablePinning = false,
  enableResizing = false,
  enableSorting = false,
  enableSelection = true,
  enableExpanding = false,
  subRowKey,
  headerActions,
  onColumnOrderChange,
}: TableHeaderProps<TData>) {
  const columnOrder =
    table.getState().columnOrder.length > 0
      ? table.getState().columnOrder
      : table.getAllColumns().map((col) => col.id);

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {}),
  );

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (active && over && active.id !== over.id) {
      const oldIndex = columnOrder.indexOf(active.id as string);
      const newIndex = columnOrder.indexOf(over.id as string);
      const newOrder = arrayMove(columnOrder, oldIndex, newIndex);

      if (onColumnOrderChange) {
        onColumnOrderChange(newOrder);
      } else {
        table.setColumnOrder(newOrder);
      }
    }
  }

  const renderHeaderContent = () => (
    <>
      {table.getHeaderGroups().map((headerGroup) => (
        <TableRow key={headerGroup.id} className="aero:border-0">
          {enableColumnDragAndDrop ? (
            <SortableContext
              items={columnOrder}
              strategy={horizontalListSortingStrategy}
            >
              {headerGroup.headers.map((header) => (
                <DraggableHeaderCell
                  key={header.id}
                  header={header}
                  table={table}
                  enableSorting={enableSorting}
                  enablePinning={enablePinning}
                  enableResizing={enableResizing}
                  enableSelection={enableSelection}
                  enableColumnDragAndDrop={enableColumnDragAndDrop}
                  enableRowDragAndDrop={enableRowDragAndDrop}
                  enableExpanding={enableExpanding}
                  subRowKey={subRowKey}
                  headerActions={headerActions}
                />
              ))}
            </SortableContext>
          ) : (
            headerGroup.headers.map((header) => {
              const column = header.column;
              const isPinned = column.getIsPinned();
              const isSelectColumn = column.id === "select";
              const isActionsColumn = column.id === "actions";
              const isSystemColumn = isSelectColumn || isActionsColumn;

              const isLastResizableColumn = (() => {
                const allColumns = table.getAllLeafColumns();
                const currentIndex = allColumns.findIndex(
                  (col) => col.id === column.id,
                );

                for (let i = currentIndex + 1; i < allColumns.length; i++) {
                  const col = allColumns[i];
                  if (col.id === "select" || col.id === "actions") continue;
                  if (col.getCanResize()) return false;
                }
                return true;
              })();

              return (
                <TableHead
                  key={header.id}
                  colSpan={header.colSpan}
                  style={{
                    position: "sticky",
                    top: 0,
                    ...getPinningStyles(column),
                    width: header.getSize(),
                  }}
                  className={cn(
                    "aero:relative aero:whitespace-nowrap aero:h-11 aero:px-2 aero:py-3 aero:border-y aero:border-neutral-200",
                    "aero:text-sm aero:font-bold aero:text-foreground aero:bg-brand-primary-50",
                    isPinned && "aero:bg-brand-primary-50",
                  )}
                >
                  {header.isPlaceholder ? null : (
                    <div className="aero:flex aero:items-center aero:gap-3">
                      {isSelectColumn && enableSelection ? (
                        <SelectionHeaderCell
                          table={table}
                          enableRowDragAndDrop={enableRowDragAndDrop}
                          enableExpanding={enableExpanding && !!subRowKey}
                        />
                      ) : isActionsColumn ? (
                        <div className="aero:flex aero:items-center aero:justify-center aero:w-full">
                          <span className="aero:font-bold aero:text-sm aero:text-foreground">
                            {flexRender(
                              column.columnDef.header,
                              header.getContext(),
                            )}
                          </span>
                        </div>
                      ) : (
                        <SortableHeaderCellContent
                          header={header}
                          enableSorting={enableSorting && column.getCanSort()}
                          enablePinning={enablePinning}
                          enableColumnDragAndDrop={false}
                        />
                      )}

                      {headerActions &&
                        !isSystemColumn &&
                        headerActions(column.id)}
                    </div>
                  )}

                  {enableResizing &&
                    !isSystemColumn &&
                    !isLastResizableColumn &&
                    column.getCanResize() && (
                      <ResizeHandle header={header} table={table} />
                    )}
                </TableHead>
              );
            })
          )}
        </TableRow>
      ))}
    </>
  );

  if (enableColumnDragAndDrop) {
    return (
      <DndContext
        collisionDetection={closestCenter}
        modifiers={[restrictToHorizontalAxis]}
        onDragEnd={handleDragEnd}
        sensors={sensors}
      >
        <TableHeaderPrimitive className="aero:bg-brand-primary-50">
          {renderHeaderContent()}
        </TableHeaderPrimitive>
      </DndContext>
    );
  }

  return (
    <TableHeaderPrimitive className="aero:bg-brand-primary-50">
      {renderHeaderContent()}
    </TableHeaderPrimitive>
  );
}

export {
  SelectionHeaderCell,
  SortableHeaderCellContent as SortableHeaderCell,
  ResizeHandle,
};
